/**
 * DASHBOARD HOME PAGE - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - StatCard components with proper Liquid Glass treatment
 * - Staggered entrance animations with spring physics
 * - Animated bar chart with glass styling
 * - Activity feed with glass list items
 * - Top partners with morphing hover states
 * - Skeleton loading states
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { DollarSign, Users, UserCircle, TrendingUp, ArrowRight, Clock, Loader2, BarChart3, ChevronRight } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../../lib/api'
import { StatCard, StatCardGrid } from '../../components/ui/StatCard'
import { GlassCard, ContentCard } from '../../components/ui/GlassCard'
import { Badge } from '../../components/ui/Badge'
import { SkeletonStatCard, SkeletonList, Skeleton } from '../../components/ui/Skeleton'
import { cn } from '../../lib/utils'

interface DashboardStats {
  totalRevenue: number
  totalPartners: number
  totalClients: number
  monthlyGrowth: number
}

interface RecentActivity {
  id: number
  action: string
  description: string
  created_at: string
}

interface TopPartner {
  id: number
  name: string
  company: string | null
  total_revenue: number
  client_count: number
}

interface MonthlyRevenue {
  month: string
  total: number
}

// Spring animation config
const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 300
}

// Staggered animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig
  }
}

export function DashboardHome() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)

  const [stats, setStats] = useState<DashboardStats>({
    totalRevenue: 0,
    totalPartners: 0,
    totalClients: 0,
    monthlyGrowth: 0
  })

  const [revenueData, setRevenueData] = useState<{ label: string; value: number }[]>([])
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([])
  const [topPartners, setTopPartners] = useState<TopPartner[]>([])

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get('/dashboard/stats')
      if (response.success) {
        setStats(response.data.stats)
        setRecentActivity(response.data.recentActivity || [])
        setTopPartners(response.data.topPartners || [])

        // Transform monthly revenue data for chart
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        const monthlyData = response.data.monthlyRevenue || []

        if (monthlyData.length > 0) {
          setRevenueData(monthlyData.map((item: MonthlyRevenue) => {
            const monthIndex = parseInt(item.month.split('-')[1]) - 1
            return {
              label: months[monthIndex],
              value: item.total
            }
          }))
        } else {
          // Default empty data for last 6 months
          const currentMonth = new Date().getMonth()
          setRevenueData(
            Array.from({ length: 6 }, (_, i) => ({
              label: months[(currentMonth - 5 + i + 12) % 12],
              value: 0
            }))
          )
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const maxRevenue = Math.max(...revenueData.map(d => d.value), 1)

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 18) return 'Good Afternoon'
    return 'Good Evening'
  }

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  const getActivityType = (action: string): 'partner' | 'client' | 'transaction' => {
    if (action.includes('LOGIN') || action.includes('OTP') || action.includes('SIGNUP')) return 'partner'
    if (action.includes('CLIENT')) return 'client'
    return 'transaction'
  }

  const getActivityColor = (type: 'partner' | 'client' | 'transaction') => {
    switch (type) {
      case 'partner': return 'purple'
      case 'client': return 'green'
      case 'transaction': return 'blue'
    }
  }

  // Loading skeleton
  if (loading) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto">
        {/* Header skeleton */}
        <div className="mb-8">
          <Skeleton width={280} height={32} rounded="lg" className="mb-2" />
          <Skeleton width={320} height={20} rounded="md" />
        </div>

        {/* Stats grid skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <SkeletonStatCard key={i} />
          ))}
        </div>

        {/* Content skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-8">
          <div className="lg:col-span-2">
            <GlassCard hoverable={false} className="h-[360px]">
              <Skeleton width="40%" height={24} rounded="md" className="mb-2" />
              <Skeleton width="30%" height={16} rounded="sm" className="mb-8" />
              <div className="flex items-end justify-between gap-4 h-[240px]">
                {[...Array(6)].map((_, i) => (
                  <Skeleton
                    key={i}
                    width="100%"
                    height={`${30 + Math.random() * 60}%`}
                    rounded="lg"
                  />
                ))}
              </div>
            </GlassCard>
          </div>
          <GlassCard hoverable={false}>
            <Skeleton width="50%" height={24} rounded="md" className="mb-6" />
            <SkeletonList items={5} />
          </GlassCard>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      className="p-6 max-w-[1600px] mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header with greeting */}
      <motion.div variants={itemVariants} className="mb-8">
        <h1 className="text-title1 font-bold text-light-text-primary dark:text-dark-text-primary mb-1.5 tracking-tight">
          {getGreeting()}, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-body text-light-text-secondary dark:text-dark-text-secondary">
          Here's what's happening with your platform today
        </p>
      </motion.div>

      {/* Stats Grid */}
      <motion.div variants={itemVariants}>
        <StatCardGrid columns={4}>
          <StatCard
            title="Total Revenue"
            value={`$${stats.totalRevenue.toLocaleString()}`}
            icon={<DollarSign className="w-6 h-6" strokeWidth={2} />}
            color="blue"
            trend={{ value: 12.5, label: 'vs last month', type: 'percentage' }}
            onClick={() => navigate('/transactions')}
          />
          <StatCard
            title="Active Partners"
            value={stats.totalPartners.toString()}
            icon={<Users className="w-6 h-6" strokeWidth={2} />}
            color="purple"
            trend={{ value: 8.3, label: 'vs last month', type: 'percentage' }}
            onClick={() => navigate('/partners')}
          />
          <StatCard
            title="Total Clients"
            value={stats.totalClients.toString()}
            icon={<UserCircle className="w-6 h-6" strokeWidth={2} />}
            color="green"
            trend={{ value: 15.7, label: 'vs last month', type: 'percentage' }}
            onClick={() => navigate('/clients')}
          />
          <StatCard
            title="Growth Rate"
            value={`${stats.monthlyGrowth}%`}
            icon={<TrendingUp className="w-6 h-6" strokeWidth={2} />}
            color="orange"
            trend={{ value: 5.2, label: 'vs last month', type: 'percentage' }}
            onClick={() => navigate('/reports')}
          />
        </StatCardGrid>
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mt-8">
        {/* Revenue Chart - Takes 2 columns */}
        <motion.div variants={itemVariants} className="lg:col-span-2">
          <ContentCard
            title="Revenue Overview"
            subtitle="Last 6 months performance"
            action={
              <motion.button
                onClick={() => navigate('/reports')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center gap-2 px-4 py-2 text-subheadline font-semibold text-light-text-secondary dark:text-dark-text-secondary hover:text-blue dark:hover:text-blue-dark rounded-xl liquid-material-clear transition-colors duration-fast"
              >
                <BarChart3 className="w-4 h-4" />
                View Details
              </motion.button>
            }
          >
            {/* Bar Chart */}
            <div className="flex items-end justify-between gap-4 mt-6" style={{ height: '260px' }}>
              {revenueData.map((item, index) => {
                const heightPercent = (item.value / maxRevenue) * 100

                return (
                  <div key={index} className="flex-1 flex flex-col items-center gap-3">
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${Math.max(heightPercent, 4)}%` }}
                      transition={{
                        duration: 0.6,
                        delay: 0.4 + index * 0.08,
                        ease: [0.23, 1, 0.32, 1]
                      }}
                      className="w-full relative group cursor-pointer"
                    >
                      {/* Bar with glass effect */}
                      <motion.div
                        className="w-full h-full rounded-xl overflow-hidden relative"
                        whileHover={{ scale: 1.05, y: -4 }}
                        transition={springConfig}
                      >
                        {/* Gradient background */}
                        <div className="absolute inset-0 bg-gradient-to-t from-blue via-blue to-blue-dark" />

                        {/* Specular highlight */}
                        <div className="absolute inset-0 bg-gradient-to-r from-white/30 via-transparent to-transparent" />

                        {/* Inner glow on hover */}
                        <div className="absolute inset-0 bg-gradient-to-t from-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      </motion.div>

                      {/* Tooltip */}
                      <AnimatePresence>
                        <motion.div
                          initial={{ opacity: 0, y: 5, scale: 0.95 }}
                          whileHover={{ opacity: 1, y: 0, scale: 1 }}
                          className="absolute -top-14 left-1/2 -translate-x-1/2 px-3 py-2 rounded-xl liquid-material-thick shadow-glass dark:shadow-dark-glass opacity-0 group-hover:opacity-100 transition-all duration-fast pointer-events-none whitespace-nowrap z-10"
                        >
                          <span className="text-subheadline font-bold text-light-text-primary dark:text-dark-text-primary">
                            ${item.value.toLocaleString()}
                          </span>
                        </motion.div>
                      </AnimatePresence>
                    </motion.div>

                    {/* Month label */}
                    <span className="text-footnote font-semibold text-light-text-secondary dark:text-dark-text-secondary">
                      {item.label}
                    </span>
                  </div>
                )
              })}
            </div>
          </ContentCard>
        </motion.div>

        {/* Recent Activity */}
        <motion.div variants={itemVariants}>
          <ContentCard
            title="Recent Activity"
            action={
              <Badge variant="blue" size="sm">
                {recentActivity.length} new
              </Badge>
            }
          >
            <div className="space-y-2 mt-4">
              {recentActivity.length > 0 ? recentActivity.slice(0, 5).map((activity, index) => {
                const activityType = getActivityType(activity.action)
                const color = getActivityColor(activityType)

                return (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.08, ...springConfig }}
                    whileHover={{
                      scale: 1.02,
                      backgroundColor: 'rgba(0, 0, 0, 0.03)',
                      transition: { duration: 0.15 }
                    }}
                    className="flex items-start gap-3 p-3 rounded-xl cursor-pointer group"
                  >
                    {/* Icon */}
                    <div className={cn(
                      'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200',
                      color === 'purple' && 'bg-purple-glass group-hover:bg-purple/20',
                      color === 'green' && 'bg-green-glass group-hover:bg-green/20',
                      color === 'blue' && 'bg-blue-glass group-hover:bg-blue/20'
                    )}>
                      {activityType === 'partner' && <Users className="w-4 h-4 text-purple dark:text-purple-dark" strokeWidth={2.5} />}
                      {activityType === 'client' && <UserCircle className="w-4 h-4 text-green dark:text-green-dark" strokeWidth={2.5} />}
                      {activityType === 'transaction' && <DollarSign className="w-4 h-4 text-blue dark:text-blue-dark" strokeWidth={2.5} />}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-subheadline font-medium text-light-text-primary dark:text-dark-text-primary mb-0.5 leading-snug line-clamp-2">
                        {activity.description}
                      </p>
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-light-text-quaternary dark:text-dark-text-quaternary" />
                        <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary font-medium">
                          {formatTimeAgo(activity.created_at)}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )
              }) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-12 h-12 rounded-2xl bg-light-bg-tertiary dark:bg-dark-bg-tertiary flex items-center justify-center mb-3">
                    <Clock className="w-6 h-6 text-light-text-quaternary dark:text-dark-text-quaternary" />
                  </div>
                  <p className="text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary">
                    No recent activity
                  </p>
                  <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mt-1">
                    Activity will appear here
                  </p>
                </div>
              )}
            </div>
          </ContentCard>
        </motion.div>
      </div>

      {/* Top Partners Section */}
      <motion.div variants={itemVariants} className="mt-8">
        <ContentCard
          title="Top Performing Partners"
          action={
            <motion.button
              onClick={() => navigate('/partners')}
              whileHover={{ scale: 1.02, x: 2 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 px-4 py-2 text-subheadline font-semibold text-blue dark:text-blue-dark hover:bg-blue-glass rounded-xl transition-colors duration-fast"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          }
        >
          <div className="space-y-2 mt-4">
            {topPartners.length > 0 ? topPartners.map((partner, index) => (
              <motion.div
                key={partner.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + index * 0.08, ...springConfig }}
                whileHover={{
                  scale: 1.01,
                  y: -2,
                  transition: { duration: 0.15 }
                }}
                whileTap={{ scale: 0.99 }}
                onClick={() => navigate(`/partners/${partner.id}`)}
                className="flex items-center gap-4 p-4 rounded-2xl liquid-material-clear cursor-pointer group"
              >
                {/* Avatar with gradient */}
                <motion.div
                  className="relative w-12 h-12 rounded-2xl overflow-hidden flex-shrink-0 shadow-glass-sm dark:shadow-dark-glass-sm"
                  whileHover={{ scale: 1.08, rotate: 3 }}
                  transition={springConfig}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue via-purple to-pink animate-liquid-shimmer bg-[length:200%_200%]" />
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent" />
                  <div className="relative w-full h-full flex items-center justify-center">
                    <span className="text-white font-bold text-subheadline tracking-tight drop-shadow-sm">
                      {partner.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                </motion.div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary mb-0.5 group-hover:text-blue dark:group-hover:text-blue-dark transition-colors duration-fast truncate">
                    {partner.name}
                  </h4>
                  <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary truncate">
                    {partner.company || 'Independent Partner'}
                  </p>
                </div>

                {/* Stats */}
                <div className="hidden sm:flex items-center gap-4">
                  <Badge variant="purple" size="sm">
                    {partner.client_count} clients
                  </Badge>
                </div>

                {/* Revenue */}
                <div className="text-right">
                  <div className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary tabular-nums">
                    ${partner.total_revenue.toLocaleString()}
                  </div>
                  <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                    total revenue
                  </p>
                </div>

                {/* Chevron */}
                <motion.div
                  initial={{ opacity: 0, x: -5 }}
                  whileHover={{ opacity: 1, x: 0 }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-fast"
                >
                  <ChevronRight className="w-5 h-5 text-light-text-quaternary dark:text-dark-text-quaternary" strokeWidth={2} />
                </motion.div>
              </motion.div>
            )) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-3xl bg-light-bg-tertiary dark:bg-dark-bg-tertiary flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-light-text-quaternary dark:text-dark-text-quaternary" />
                </div>
                <p className="text-headline font-semibold text-light-text-secondary dark:text-dark-text-secondary mb-1">
                  No partners yet
                </p>
                <p className="text-subheadline text-light-text-tertiary dark:text-dark-text-tertiary max-w-sm">
                  Add partners to see their performance here
                </p>
                <motion.button
                  onClick={() => navigate('/partners')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="mt-4 px-6 py-2.5 rounded-xl bg-blue dark:bg-blue-dark text-white font-semibold text-subheadline shadow-glass-sm"
                >
                  Add Partners
                </motion.button>
              </div>
            )}
          </div>
        </ContentCard>
      </motion.div>
    </motion.div>
  )
}
